import { useCallback, useEffect, useState } from "react";

import { Button, EmptyState, ErrorState, Notice, SkeletonRows, Status } from "../../../../components/ui";
import { focusRing } from "../../../../components/ui/cn";
import { useBusinessAuth } from "../../../context/BusinessAuthContext";
import { REQUEST_STATUS, errorText } from "../myHrText";
import RequestDecisionDialog from "./RequestDecisionDialog";

const FILTERS = [
  { key: "pending", label: "Waiting" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

const TONE = { approved: "success", rejected: "danger" };
const DONE = { approve: "approved", reject: "rejected", withdraw: "withdrawn" };

const sentAt = (value) => (value
  ? new Date(value).toLocaleString("en-PH", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
  : "");

/**
 * Review queue for one kind of employee request (attendance corrections, overtime, schedule changes). Each kind
 * keeps its own API and its own status; this renders the list and the decision. The server decides who may approve —
 * the buttons are hidden on your own request unless you are the owner, who may decide it with a reason when nobody
 * else can. `kind` names the request in the dialog ("attendance correction").
 */
export default function RequestReviewList({
  load, decide, summarize, approveText, emptyText, onPendingCount, reloadKey = 0, canDecide = true, withdrawLabel = null,
  kind = "request",
}) {
  const auth = useBusinessAuth();
  const myMemberId = Number(auth?.currentBusiness?.membership_id || 0);
  const isOwner = String(auth?.role || "").toUpperCase() === "OWNER";
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [deciding, setDeciding] = useState(null); // { request, decision }
  const [notice, setNotice] = useState("");

  const refresh = useCallback(() => {
    setError("");
    load({ status: filter })
      .then((res) => {
        const list = res?.requests || [];
        setRows(list);
        if (filter === "pending") onPendingCount?.(list.length);
      })
      .catch((err) => { setRows([]); setError(errorText(err, "Check your connection and try again.")); });
  }, [filter, load, onPendingCount]);
  useEffect(() => { refresh(); }, [refresh, reloadKey]);

  const who = (request) => request.memberName || `Employee #${request.memberId}`;
  const close = () => { setDeciding(null); refresh(); };
  const submit = async ({ note, overrideReason }) => {
    const { request, decision } = deciding;
    const body = { ...(note ? { note } : {}), ...(overrideReason ? { override_reason: overrideReason } : {}) };
    await decide(request.id, decision, body);
    setDeciding(null);
    setNotice(`${who(request)}'s ${kind} was ${DONE[decision]}. They can see the decision in My HR.`);
    refresh();
  };

  const dialog = () => {
    const { request, decision } = deciding;
    const facts = [{ label: "Employee", value: who(request) }, { label: "Sent", value: sentAt(request.createdAt) || "—" }];
    const shared = { facts, onSubmit: submit, onClose: close };
    const body = (
      <>
        <div className="text-foreground">{summarize(request)}</div>
        {request.reason ? <p>Their reason: {request.reason}</p> : null}
      </>
    );
    if (decision === "approve") {
      return (
        <RequestDecisionDialog {...shared} title={`Approve ${who(request)}'s ${kind}?`} confirmLabel={`Approve ${kind}`} consequence={approveText(request)} failureTitle="Not approved">
          {body}
        </RequestDecisionDialog>
      );
    }
    if (decision === "reject") {
      return (
        <RequestDecisionDialog
          {...shared}
          title={`Reject ${who(request)}'s ${kind}?`}
          confirmLabel={`Reject ${kind}`}
          consequence="Nothing changes for the employee. They can send a new request."
          reason={{ label: "Why? The employee sees this.", required: true }}
          failureTitle="Not rejected"
        >
          {body}
        </RequestDecisionDialog>
      );
    }
    return (
      <RequestDecisionDialog
        {...shared}
        tone="danger"
        title={`${withdrawLabel} for ${who(request)}?`}
        confirmLabel={withdrawLabel}
        cancelLabel="Keep it approved"
        consequence="The approval is taken back. What it allowed no longer applies."
        reason={{ label: "Why? The employee sees this.", required: true }}
        failureTitle="Not withdrawn"
      >
        {body}
      </RequestDecisionDialog>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1" role="group" aria-label="Show requests">
        {FILTERS.map((item) => (
          <button
            key={item.key}
            type="button"
            data-ui="button"
            onClick={() => setFilter(item.key)}
            aria-pressed={filter === item.key}
            className={`inline-flex h-control-sm items-center rounded-control px-3 text-control-sm max-md:min-h-touch ${focusRing} ${filter === item.key ? "bg-accent-subtle text-accent" : "text-secondary hover:bg-hover hover:text-foreground"}`}
          >
            {item.label}
          </button>
        ))}
      </div>
      {notice ? <Notice tone="success" role="status">{notice}</Notice> : null}
      {error ? (
        <div className="rounded-panel border border-default bg-surface">
          <ErrorState title="The requests couldn't be loaded" description={error} onRetry={refresh} />
        </div>
      ) : !rows ? (
        <div className="rounded-panel border border-default bg-surface"><SkeletonRows rows={3} columns={3} label="Loading requests" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-panel border border-default bg-surface">
          <EmptyState title={filter === "pending" ? emptyText : "No requests here."} />
        </div>
      ) : (
        <ul className="divide-y divide-subtle rounded-panel border border-default bg-surface">
          {rows.map((request) => {
            const own = request.memberId === myMemberId;
            const status = REQUEST_STATUS[request.status] || REQUEST_STATUS.pending;
            const tone = TONE[request.status] || "neutral";
            return (
              <li key={request.id} className="space-y-1 px-4 py-3 text-body-sm">
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <p className="text-panel-title text-foreground">{who(request)}{own ? " (you)" : ""}</p>
                  <Status tone={tone} icon={tone !== "neutral"} className={tone === "neutral" ? "text-secondary" : "font-medium"}>{status.label}</Status>
                </div>
                <div className="text-foreground">{summarize(request)}</div>
                <p className="text-secondary">Reason: {request.reason}</p>
                <p className="text-meta text-tertiary">Sent {sentAt(request.createdAt)}</p>
                {request.status !== "pending" && request.reviewerName ? (
                  <p className="text-secondary">
                    {status.label} by {request.reviewerName}{request.reviewNote ? `: “${request.reviewNote}”` : ""}
                    {request.ownerOverride ? ` · Owner decision: ${request.ownerOverrideReason}` : ""}
                  </p>
                ) : null}
                {request.status === "pending" && canDecide ? (
                  own && !isOwner ? (
                    <p className="text-meta text-secondary">Your own request — someone else has to review it.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button size="sm" variant="primary" onClick={() => setDeciding({ request, decision: "approve" })}>Approve</Button>
                      <Button size="sm" onClick={() => setDeciding({ request, decision: "reject" })}>Reject</Button>
                    </div>
                  )
                ) : null}
                {request.status === "approved" && canDecide && withdrawLabel ? (
                  <Button size="sm" className="mt-1" onClick={() => setDeciding({ request, decision: "withdraw" })}>{withdrawLabel}</Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {deciding ? dialog() : null}
    </div>
  );
}
